import React, { createContext, useState, useEffect } from 'react';
import { hasPermission, hasRole } from '../utils/permission'; // Funções utilitárias para verificar permissões e papéis.
import { CircularProgress } from '@mui/material'; // Componente para mostrar um indicador de carregamento.

export const PermissionsContext = createContext(); // Criação do contexto de permissões.

export const PermissionsProvider = ({ children }) => {
    // Estados para armazenar papéis e permissões do usuário, e o status de carregamento.
    const [userRoles, setUserRoles] = useState([]); 
    const [userPermissions, setUserPermissions] = useState([]);
    const [loading, setLoading] = useState(true); // Status de carregamento da informação.

    /**
     * useEffect responsável por carregar os papéis e permissões do usuário do localStorage.
     * Realiza a leitura quando o componente é montado.
     */
    useEffect(() => {
        const fetchRolesAndPermissions = () => {
            setLoading(true); // Inicia o carregamento.

            const storedRoles = localStorage.getItem('userRoles'); // Obtém os papéis do localStorage.
            const storedPermissions = localStorage.getItem('userPermissions'); // Obtém as permissões do localStorage.

            try {
                // Se os papéis estiverem no localStorage, os armazena no estado.
                if (storedRoles) {
                    setUserRoles(JSON.parse(storedRoles));
                } else {
                    setUserRoles([]);  
                }

                // Se as permissões estiverem no localStorage, as armazena no estado.
                if (storedPermissions) {
                    setUserPermissions(JSON.parse(storedPermissions));
                } else {
                    setUserPermissions([]);  
                }
            } catch (error) {
                console.error('Erro ao carregar dados do localStorage', error); // Caso ocorra erro ao carregar, exibe no console.
            } finally {
                setLoading(false); // Finaliza o carregamento.
            }
        };

        fetchRolesAndPermissions(); // Chama a função para buscar as permissões e papéis.
    }, []); // Executa apenas uma vez quando o componente é montado.

    /**
     * Função para verificar se o usuário tem uma permissão específica.
     * 
     * @param {string} requiredPermission - Permissão necessária para acesso.
     * @returns {boolean} - Retorna true se o usuário tem a permissão, caso contrário false.
     */
    const canAccess = (requiredPermission) => {
        return hasPermission(userRoles, userPermissions, requiredPermission); // Verifica a permissão.
    };

    /**
     * Função para verificar se o usuário tem um papel específico.
     * 
     * @param {array} rolesName - Lista de papéis necessários.
     * @param {array} rolesUser - Papéis do usuário.
     * @returns {boolean} - Retorna true se o usuário tem um dos papéis fornecidos, caso contrário false.
     */
    const UserHasRole = (rolesName, rolesUser) => {
        return hasRole(rolesName, rolesUser); // Verifica se o usuário tem o papel necessário.
    };

    /**
     * Função para adicionar novos papéis ao usuário e armazená-los no localStorage.
     * 
     * @param {array} roles - Lista de papéis do usuário.
     */
    const addRoles = (roles) => {
        localStorage.setItem('userRoles', JSON.stringify(roles)); // Armazena os papéis no localStorage.
        setUserRoles(roles); // Atualiza o estado com os novos papéis.
    };

    /**
     * Função para adicionar novas permissões ao usuário e armazená-las no localStorage.
     * 
     * @param {array} permissions - Lista de permissões do usuário.
     */
    const addPermissions = (permissions) => {
        localStorage.setItem('userPermissions', JSON.stringify(permissions)); // Armazena as permissões no localStorage.
        setUserPermissions(permissions); // Atualiza o estado com as novas permissões.
    };

    // Se o estado ainda estiver carregando, exibe um indicador de progresso.
    if (loading) {
        return <div style={{width:'100vw', height:'100vh', display:'flex', justifyContent:'center', alignItems:'center'}}><CircularProgress/></div>;
    }

    return (
        <PermissionsContext.Provider
            value={{
                userRoles, // Papéis do usuário.
                userPermissions, // Permissões do usuário.
                canAccess, // Função para verificar permissões.
                addRoles, // Função para adicionar papéis ao usuário.
                addPermissions, // Função para adicionar permissões ao usuário.
                UserHasRole // Função para verificar os papéis do usuário.
            }}
        >
            {children} {/* Renderiza os filhos com o contexto de permissões fornecido. */}
        </PermissionsContext.Provider>
    );
};
